'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box } from '@terra/ui-styled-system';
import type { SelectionInfo } from '@terra/ui-design-system';
import { SendToChatIconButton } from '../SendToChatIconButton';

const SELECTION_TOOLBAR_HEIGHT_PX = 28;
const SELECTION_TOOLBAR_GAP_PX = 8;
const SELECTION_TOOLBAR_OFFSET_Y_PX =
  SELECTION_TOOLBAR_HEIGHT_PX + SELECTION_TOOLBAR_GAP_PX;

type SelectionAction = {
  x: number;
  y: number;
  text: string;
  lineStart?: number;
  lineEnd?: number;
};

type UseCodeMirrorSendToChatToolbarParams = {
  sendToChatLabel: string;
  onSendSelection: ({
    excerpt,
    lineStart,
    lineEnd,
  }: {
    excerpt: string;
    lineStart?: number;
    lineEnd?: number;
  }) => void;
};

export function useCodeMirrorSendToChatToolbar({
  sendToChatLabel,
  onSendSelection,
}: UseCodeMirrorSendToChatToolbarParams): {
  handleSelectionChange: (selection: SelectionInfo) => void;
  selectionToolbar: ReactNode;
} {
  const toolbarButtonRef = useRef<HTMLButtonElement>(null);
  const [selectionAction, setSelectionAction] =
    useState<SelectionAction | null>(null);

  const clearSelectionAction = useCallback(() => {
    setSelectionAction(null);
  }, []);

  const handleSelectionChange = useCallback(
    (selection: SelectionInfo) => {
      const excerpt = selection.text.trim();

      if (!excerpt || !selection.coords) {
        clearSelectionAction();

        return;
      }

      setSelectionAction({
        x: selection.coords.left,
        y: selection.coords.top - SELECTION_TOOLBAR_OFFSET_Y_PX,
        text: selection.text,
        lineStart: selection.lineStart,
        lineEnd: selection.lineEnd,
      });
    },
    [clearSelectionAction],
  );

  const handleSendSelection = useCallback(() => {
    if (!selectionAction?.text) {
      return;
    }

    const { text, lineStart, lineEnd } = selectionAction;

    clearSelectionAction();
    onSendSelection({
      excerpt: text,
      lineStart,
      lineEnd,
    });
  }, [clearSelectionAction, onSendSelection, selectionAction]);

  useEffect(() => {
    if (!selectionAction) {
      return;
    }

    const closeSelectionAction = (event: PointerEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        toolbarButtonRef.current?.contains(target)
      ) {
        return;
      }

      clearSelectionAction();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelectionAction();
      }
    };

    document.addEventListener('pointerdown', closeSelectionAction);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', closeSelectionAction);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearSelectionAction, selectionAction]);

  const selectionToolbar = selectionAction ? (
    <Box
      position="fixed"
      left={`${selectionAction.x}px`}
      top={`${selectionAction.y}px`}
      zIndex={9999}
    >
      <SendToChatIconButton
        label={sendToChatLabel}
        buttonRef={toolbarButtonRef}
        variant="outline"
        iconSize={14}
        onPointerDown={(event) => event.preventDefault()}
        onMouseDown={(event) => event.preventDefault()}
        onClick={handleSendSelection}
      />
    </Box>
  ) : null;

  return { handleSelectionChange, selectionToolbar };
}
